import { upperCase } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@config/axios/axios.config';
import { paths } from '@common/types';
import axios from 'axios';
import { stableKey } from '@common/cache/util.ts';

interface IOrganizationListProps {
  page: number;
  limit: number;
  sort_by?: string;
  sort_order?: string;
  search: string;
}

const organizationsRequest = async (props: IOrganizationListProps) => {
  const response = axiosInstance.get<
    paths[`/record/organizations`]['get']['responses']['200']['content']['application/json']
  >(`/record/organizations`, {
    params: {
      ...props,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
    },
  });
  const data = (await response).data;

  return data;
};

export const useOrganizationsQuery = (props: IOrganizationListProps) => {
  const query = useQuery({
    queryKey: ['organizations', stableKey(props)],
    queryFn: () => organizationsRequest(props),
    placeholderData: keepPreviousData,
  });

  return query;
};

const organizationsShortRequest = async () => {
  const response = axiosInstance.get<
    paths[`/record/organizations/short`]['get']['responses']['200']['content']['application/json']
  >(`/record/organizations/short`);
  const data = (await response).data;
  return data;
};

export const useOrganizationsShortQuery = (enabled = true) => {
  const orgQuery = useQuery({
    enabled,
    queryKey: ['organizationShort'],
    queryFn: () => organizationsShortRequest(),
  });

  return orgQuery;
};

interface IOrganizationProps {
  organization_id: string;
}

const organizationRequest = async (props: IOrganizationProps) => {
  const response = axiosInstance.get<
    paths[`/organization/{record_id}`]['get']['responses']['200']['content']['application/json']
  >(`/organization/${props.organization_id}`);
  const data = (await response).data;

  return data;
};

export const useOrganizationQuery = (props: IOrganizationProps) => {
  const query = useQuery({
    queryKey: ['organization', props.organization_id],
    queryFn: () => organizationRequest(props),
  });

  return query;
};

interface IOrganizationHistoryProps {
  page: number;
  limit: number;
  organization_id: string;
  enabled: boolean;
}

const organizationHistoryRequest = async (props: IOrganizationHistoryProps) => {
  const response = axiosInstance.get<
    paths[`/organization/{record_id}/history`]['get']['responses']['200']['content']['application/json']
  >(`/organization/${props.organization_id}/history`, {
    params: {
      page: props.page,
      limit: props.limit,
    },
  });
  const data = (await response).data;

  return data;
};

export const useOrganizationHistoryQuery = (
  props: IOrganizationHistoryProps,
) => {
  const query = useQuery({
    enabled: !!props.enabled,
    queryKey: ['organization', props.organization_id, 'history', props.page],
    queryFn: () => organizationHistoryRequest(props),
  });

  return query;
};

interface IOrganizationLogoProps {
  organization_id: string;
}

const organizationLogoRequest = async (
  props: IOrganizationLogoProps,
  signal?: AbortSignal,
): Promise<Blob | null> => {
  try {
    const response = await axiosInstance.get<Blob>(
      `/organization/${props.organization_id}/logo.webp`,
      {
        headers: { 'Content-type': 'image/webp' },
        responseType: 'blob',
        signal,
        skipNotFoundRedirect: true,
      },
    );
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
};

export const useOrganizationLogoQuery = (props: IOrganizationLogoProps) => {
  const query = useQuery({
    queryKey: ['organization', props.organization_id, 'logo'],
    queryFn: () => organizationLogoRequest(props),
  });

  return query;
};
